import type React from "react";
import { useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { ProductCatalog } from "@/components/catalog/product-catalog";
import { CatalogueList } from "@/components/catalog/catalogue-list";
import { CreateCatalogueButton } from "@/components/catalog/create-catalogue-button";
import { CategoryManagement } from "@/components/catalog/categories/category-management";
import { ModerationManagement } from "@/components/catalog/moderation/moderation-management";
import { ReviewQueue } from "@/components/catalog/review/review-queue";
import { CatalogueSettings } from "@/components/catalog/catalogue-settings";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ImportProductsModal } from "@/components/catalog/import-products-modal";
import { ArrowLeft, Upload } from "lucide-react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  CatalogueService,
  catalogueCacheKey,
  type Catalogue,
} from "@/services/catalogue.service";

const detailTabTriggerClassName =
  "h-12 shrink-0 rounded-none border-b-2 border-transparent bg-transparent px-0 text-sm font-medium text-muted-foreground shadow-none transition-colors hover:text-foreground data-[state=active]:border-pink-500 data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none";

function getCatalogueStatus(catalogue?: Catalogue) {
  if (!catalogue) return null;

  if (catalogue.provisioningStatus === "provisioning") {
    return {
      label: "Preparing",
      className:
        "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300",
    };
  }

  if (catalogue.provisioningStatus === "failed") {
    return {
      label: "Provisioning failed",
      className:
        "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300",
    };
  }

  if (catalogue.deletedAt) {
    return {
      label: "Deleted",
      className:
        "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300",
    };
  }

  if (catalogue.enabled === true) {
    return {
      label: "Active",
      className:
        "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300",
    };
  }

  if (catalogue.enabled === false) {
    return {
      label: "Disabled",
      className: "border-border bg-muted text-muted-foreground",
    };
  }

  return null;
}

const CatalogPage: React.FC = () => {
  const { catalogueId } = useParams<{ catalogueId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [catalogueCount, setCatalogueCount] = useState<number>(0);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const tabScrollerRef = useRef<HTMLDivElement>(null);
  const {
    data: catalogue,
    error: catalogueError,
    isLoading: isCatalogueLoading,
    mutate: mutateCatalogue,
  } = useSWR<Catalogue>(
    catalogueId ? catalogueCacheKey(catalogueId) : null,
    () => CatalogueService.getById(catalogueId!),
    {
      revalidateOnFocus: false,
      refreshInterval: (latest) => latest?.provisioningStatus === "provisioning" ? 5_000 : 0,
      refreshWhenHidden: false,
    }
  );

  const catalogueName = catalogue?.name ?? "";
  const catalogueDeletedAt = catalogue?.deletedAt;
  const catalogueLanguages = catalogue?.languages;
  const requestedTab = searchParams.get("tab");
  const catalogueReady = catalogue?.provisioningStatus === "ready";
  const supportsProductRead = catalogueReady && (catalogue?.capabilities.includes("product.read") ?? false);
  const supportsProductIngest = catalogueReady && (catalogue?.capabilities.includes("product.ingest") ?? false);
  const supportedTabs = [
    ...(supportsProductRead ? ["products"] : []),
    ...(catalogueReady && catalogue?.capabilities.includes("review") ? ["review"] : []),
    ...(catalogueReady && catalogue?.capabilities.includes("categorization") ? ["categories"] : []),
    ...(catalogueReady && catalogue?.capabilities.includes("moderation") ? ["moderation"] : []),
    "settings",
  ];
  const activeTab = supportedTabs.includes(requestedTab ?? "")
    ? requestedTab!
    : supportedTabs[0] ?? "settings";

  useEffect(() => {
    const activeTrigger = tabScrollerRef.current?.querySelector<HTMLElement>(
      '[role="tab"][aria-selected="true"]'
    );
    activeTrigger?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeTab]);

  const handleViewCatalog = (id: string) => {
    navigate(`/catalog/${id}`);
  };

  const handleCatalogueCountChange = (count: number) => {
    setCatalogueCount(count);
  };

  const handleCatalogueUpdated = (updated: Catalogue) => {
    void mutateCatalogue(updated, { revalidate: false });
  };

  if (!catalogueId) {
    return (
      <div className="flex-1 space-y-4 p-2">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-3xl font-bold tracking-tight text-transparent">
              Catalogue Management
            </h2>
            <p className="text-muted-foreground">Manage your product catalogues</p>
          </div>
          {catalogueCount > 0 ? <CreateCatalogueButton /> : null}
        </div>

        <CatalogueList
          onViewCatalog={handleViewCatalog}
          onCatalogueCountChange={handleCatalogueCountChange}
        />
      </div>
    );
  }

  if (catalogueError && !catalogue) {
    return (
      <div className="flex min-h-[420px] flex-col items-center justify-center rounded-lg border bg-card p-8 text-center">
        <h1 className="text-xl font-semibold">Catalogue could not be loaded</h1>
        <p className="mt-2 max-w-md text-sm text-muted-foreground">
          Check your connection and try again. Catalogue actions stay unavailable until its settings load successfully.
        </p>
        <div className="mt-6 flex gap-2">
          <Button variant="outline" onClick={() => navigate("/catalog")}>Back to catalogues</Button>
          <Button onClick={() => void mutateCatalogue()}>Try again</Button>
        </div>
      </div>
    );
  }

  const catalogueStatus = getCatalogueStatus(catalogue);

  return (
    <div className="min-w-0 flex-1 p-2">
      <Tabs
        value={activeTab}
        onValueChange={(tab) =>
          setSearchParams(tab === "products" ? {} : { tab }, { replace: true })
        }
        className="min-w-0"
      >
        <section
          aria-label="Catalogue details"
          className="min-w-0"
        >
          <div className="pb-5">
            <div className="flex min-w-0 items-center justify-between gap-3">
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="h-8 px-0 text-muted-foreground hover:bg-transparent hover:text-foreground"
              >
                <Link to="/catalog">
                  <ArrowLeft aria-hidden="true" className="h-4 w-4" />
                  Back to catalogues
                </Link>
              </Button>

              <div className="flex shrink-0 items-center gap-2">
                {catalogueStatus ? (
                  <Badge variant="outline" className={catalogueStatus.className}>
                    {catalogueStatus.label}
                  </Badge>
                ) : null}
                <Button
                  size="sm"
                  onClick={() => setIsImportModalOpen(true)}
                  disabled={!catalogueName || !catalogueReady || !!catalogueDeletedAt || !supportsProductIngest}
                  title={!catalogueReady
                    ? catalogue?.provisioningStatus === "failed"
                      ? "Catalogue provisioning failed"
                      : "Catalogue is still being prepared"
                    : !supportsProductIngest
                    ? "This catalogue does not include product ingestion"
                    : catalogueDeletedAt
                      ? "Cannot import: Catalogue scheduled for deletion"
                      : undefined}
                  className="gap-1 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Upload className="h-4 w-4" />
                  Import Products
                </Button>
              </div>
            </div>

            <div className="mt-4 min-w-0">
              {isCatalogueLoading ? (
                <div className="space-y-3" role="status" aria-label="Loading catalogue">
                  <Skeleton className="h-8 w-3/4 max-w-2xl" />
                  <Skeleton className="h-4 w-1/2 max-w-xl" />
                </div>
              ) : (
                <>
                  <h1
                    id="catalogue-title"
                    className="break-words text-2xl font-semibold tracking-tight text-foreground"
                  >
                    {catalogue?.name || "Catalogue unavailable"}
                  </h1>

                  {catalogue?.description?.trim() ? (
                    <p className="mt-2 max-w-3xl text-sm leading-6 text-muted-foreground">
                      {catalogue.description}
                    </p>
                  ) : null}
                </>
              )}
            </div>
          </div>

          <div
            ref={tabScrollerRef}
            className="overflow-x-auto border-b [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <TabsList
              aria-label="Catalogue sections"
              className="h-auto w-full min-w-max justify-start gap-6 rounded-none bg-transparent p-0"
            >
              {supportsProductRead ? <TabsTrigger className={detailTabTriggerClassName} value="products">
                Products
              </TabsTrigger> : null}
              {catalogueReady && catalogue?.capabilities.includes("review") ? (
                <TabsTrigger className={detailTabTriggerClassName} value="review">
                  Review
                </TabsTrigger>
              ) : null}
              {catalogueReady && catalogue?.capabilities.includes("categorization") ? (
                <TabsTrigger className={detailTabTriggerClassName} value="categories">
                  Categories
                </TabsTrigger>
              ) : null}
              {catalogueReady && catalogue?.capabilities.includes("moderation") ? (
                <TabsTrigger className={detailTabTriggerClassName} value="moderation">
                  Moderation
                </TabsTrigger>
              ) : null}
              <TabsTrigger className={detailTabTriggerClassName} value="settings">
                Settings
              </TabsTrigger>
            </TabsList>
          </div>
        </section>

        <div className="mt-6 min-w-0">
          {catalogueError ? (
            <div
              role="alert"
              className="mb-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            >
              Failed to load catalogue details. The catalogue sections may be incomplete.
            </div>
          ) : null}

          {supportsProductRead ? <TabsContent className="mt-0 min-w-0" value="products">
            <ProductCatalog
              catalogueId={catalogueId}
              catalogueName={catalogueName}
              deletedAt={catalogueDeletedAt}
              capabilities={catalogue?.capabilities ?? []}
            />
          </TabsContent> : null}
          {catalogueReady && catalogueLanguages && catalogue?.capabilities.includes("categorization") ? <TabsContent className="mt-0 min-w-0" value="categories">
            <CategoryManagement
              catalogueId={catalogueId}
              deletedAt={catalogueDeletedAt}
              languages={catalogueLanguages}
            />
          </TabsContent> : null}
          {catalogueReady && catalogue?.capabilities.includes("moderation") ? <TabsContent className="mt-0 min-w-0" value="moderation">
            <ModerationManagement catalogueId={catalogueId} deletedAt={catalogueDeletedAt} />
          </TabsContent> : null}
          {catalogueReady && catalogue?.capabilities.includes("review") ? <TabsContent className="mt-0 min-w-0" value="review">
            <ReviewQueue catalogueId={catalogueId} deletedAt={catalogueDeletedAt} />
          </TabsContent> : null}
          <TabsContent className="mt-0 min-w-0" value="settings">
            {catalogue ? (
              <CatalogueSettings key={catalogue.id} catalogue={catalogue} onUpdated={handleCatalogueUpdated} />
            ) : (
              <p className="text-sm text-muted-foreground">Loading catalogue settings…</p>
            )}
          </TabsContent>
        </div>
      </Tabs>

      {supportsProductIngest ? <ImportProductsModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        catalogueId={catalogueId!}
        catalogueName={catalogueName}
      /> : null}
    </div>
  );
};

export default CatalogPage;
